import { stripTime } from './taskSchedule';

const DAY_MS = 86400000;
export const GANTT_RANGE_PAD_BEFORE_DAYS = 14;
/** Empty space after the last scheduled task / milestone so the default view does not clip at the bar. */
export const GANTT_RANGE_PAD_AFTER_MONTHS = 4;
export const GANTT_EMPTY_RANGE_BEFORE_DAYS = 14;
export const GANTT_EMPTY_RANGE_AFTER_DAYS = 90;
/** Ignore overlay-scrollbar sized viewport jitter so Fit mode does not re-zoom on scroll. */
export const GANTT_FIT_VIEWPORT_HYSTERESIS_PX = 20;

function addCalendarMonths(ms: number, months: number): Date {
  const d = new Date(ms);
  d.setMonth(d.getMonth() + months);
  return stripTime(d);
}

function toMs(value: unknown): number | null {
  if (value == null) return null;
  const t = new Date(value as Date).getTime();
  return Number.isFinite(t) ? t : null;
}

export function ganttFitCellWidth(viewportWidth: number, dayCount: number, min: number, max: number): number {
  const n = Math.max(1, dayCount);
  const w = Math.max(0, viewportWidth);
  return Math.max(min, Math.min(max, Math.floor(w / n)));
}

export function ganttFitViewportChangedEnough(prevWidth: number, nextWidth: number): boolean {
  return Math.abs(nextWidth - prevWidth) >= GANTT_FIT_VIEWPORT_HYSTERESIS_PX;
}

export type GanttDateRangeMs = { startMs: number; endMs: number; hasAnchors: boolean };

/** Keep the widest range seen this session so task edits do not shrink the axis. */
export function expandGanttDateRangeMs(prev: GanttDateRangeMs, next: GanttDateRangeMs): GanttDateRangeMs {
  if (!next.hasAnchors) {
    return prev.hasAnchors ? prev : next;
  }
  if (!prev.hasAnchors) return next;
  return {
    startMs: Math.min(prev.startMs, next.startMs),
    endMs: Math.max(prev.endMs, next.endMs),
    hasAnchors: true,
  };
}

export function computeGanttDateRangeMs(opts: {
  taskTimes: unknown[];
  milestoneTimes?: unknown[];
  extraTimes?: unknown[];
  nowMs?: number;
}): GanttDateRangeMs {
  const all: number[] = [];
  for (const value of opts.taskTimes) {
    const t = toMs(value);
    if (t != null) all.push(t);
  }
  for (const value of opts.milestoneTimes ?? []) {
    const t = toMs(value);
    if (t != null) all.push(t);
  }
  for (const value of opts.extraTimes ?? []) {
    const t = toMs(value);
    if (t != null) all.push(t);
  }

  if (all.length === 0) {
    const now = opts.nowMs ?? Date.now();
    return {
      startMs: stripTime(new Date(now - GANTT_EMPTY_RANGE_BEFORE_DAYS * DAY_MS)).getTime(),
      endMs: stripTime(new Date(now + GANTT_EMPTY_RANGE_AFTER_DAYS * DAY_MS)).getTime(),
      hasAnchors: false,
    };
  }

  const min = Math.min(...all);
  const max = Math.max(...all);
  return {
    startMs: stripTime(new Date(min - GANTT_RANGE_PAD_BEFORE_DAYS * DAY_MS)).getTime(),
    endMs: addCalendarMonths(max, GANTT_RANGE_PAD_AFTER_MONTHS).getTime(),
    hasAnchors: true,
  };
}

export function buildGanttDays(startMs: number, endMs: number): Date[] {
  const result: Date[] = [];
  const current = stripTime(new Date(startMs));
  const end = stripTime(new Date(endMs));
  while (current.getTime() <= end.getTime()) {
    result.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  return result;
}
